import { FetchApiClient } from '../fetch-api-client'

// Routed behind the Skedulo API gateway alongside the other REST services,
// e.g. `{SKEDULO_BASE_URL}/policy/evaluate`.
const BASE_PATH = 'policy'

export type Disposition = 'allow' | 'warn' | 'block' | 'approval_required'

export interface DecisionRequestProposedAction {
  type: 'shift-assign'
  shiftId: string
  resourceId: string
  /** ISO-8601 UTC. */
  start: string
  /** ISO-8601 UTC. */
  end: string
}

export interface DecisionRequest {
  tenantId: string
  /** Has no actor: the engine records the authenticated principal itself. */
  context: {
    type: 'shift-assign'
    proposedAction: DecisionRequestProposedAction
  }
  /** Optional time-travel; defaults to now server-side. */
  evaluateAtTime?: string
}

export interface DecisionReason {
  severity: 'block' | 'warn' | 'info'
  message: string
}

export interface DecisionRuleHit {
  reasons: DecisionReason[]
}

// Trimmed to the fields the roster UI reads. The transport (`FetchApiClient.post`)
// casts without runtime schema validation, so unread fields (createdAt,
// evaluationMs, factHash, …) are ignored, not rejected.
export interface Decision {
  id: string
  primaryDisposition: Disposition
  contextRef: { id: string; type: string }
  ruleHits?: DecisionRuleHit[]
  /** Human-readable rendering of the decision, citing the rules/clauses that
   *  fired (e.g. award / enterprise-agreement clauses). Preferred for UI. */
  explanationPayload?: { rendered?: string }
}

export interface UnbaselinedDomain {
  factDomain: string
  /** Open enum: `entity-unseen`, `domain-not-baselined`; other values are possible. */
  reason: string
}

/**
 * The engine's answer when it does not yet hold complete data for the tenant,
 * for example while a new tenant's first data load runs. It is not a verdict:
 * the engine did not evaluate any rule, so a caller must not treat it as a block.
 */
export interface WarmingResult {
  kind: 'warming'
  tenantId: string
  unbaselinedDomains: UnbaselinedDomain[]
}

/** What a successful `evaluate` returns: a verdict, or a warming result. */
export type PolicyEvaluation = Decision | WarmingResult

export interface PolicyEvaluateOptions {
  signal?: AbortSignal
}

export function isWarming(
  evaluation: PolicyEvaluation,
): evaluation is WarmingResult {
  return 'kind' in evaluation && evaluation.kind === 'warming'
}

export class PolicyEngineApiClient {
  constructor(protected readonly client: FetchApiClient) {}

  evaluate(
    request: DecisionRequest,
    { signal }: PolicyEvaluateOptions = {},
  ): Promise<PolicyEvaluation> {
    // `rawResponse: true` — the policy engine returns the Decision at the top
    // level, NOT wrapped in the `{ result }` envelope `fetchJson` unwraps by
    // default. Without this the call resolves to `undefined`.
    return this.client.post<PolicyEvaluation>(
      `${BASE_PATH}/evaluate`,
      {
        body: JSON.stringify(request),
        headers: { 'Content-Type': 'application/json' },
        signal,
      },
      { rawResponse: true },
    )
  }
}
